import { FC, useEffect, useState } from 'react';
import { MarketStats } from '@/types/index';
import { propertyService } from '@/services/property.service';

function fmt(n: number) {
  return new Intl.NumberFormat('fr-CH').format(Math.round(n));
}

export const MarketStatsWidget: FC = () => {
  const [stats, setStats] = useState<MarketStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    propertyService
      .getMarketStats()
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 h-48" />
    );
  }

  if (!stats || stats.totalTracked === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <p className="text-gray-500 dark:text-gray-400 text-sm text-center">
          Aucune donnée marché disponible. Lancez un harvest pour commencer le suivi.
        </p>
      </div>
    );
  }

  const { ageDistribution: age } = stats;
  const activeTotal = age.lt14 + age.lt60 + age.lt180 + age.gt180;
  const pct = (n: number) => (activeTotal > 0 ? Math.round((n / activeTotal) * 100) : 0);

  return (
    <div className="space-y-4">
      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KpiCard label="Annonces suivies" value={stats.totalTracked} />
        <KpiCard label="Actives" value={stats.totalActive} color="green" />
        <KpiCard label="Off-market" value={stats.totalOffMarket} color="gray" />
        <KpiCard
          label="Durée moy. marché"
          value={stats.avgDaysOnMarket != null ? `${stats.avgDaysOnMarket}j` : '—'}
          color="blue"
        />
      </div>

      {/* Distribution par âge */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
          Annonces actives — âge sur le marché
        </h3>
        {activeTotal === 0 ? (
          <p className="text-sm text-gray-400">Aucune annonce active.</p>
        ) : (
          <div className="space-y-2">
            <AgeBar label="Fraîches (< 14j)" count={age.lt14} pct={pct(age.lt14)} color="bg-green-500" />
            <AgeBar label="Récentes (14–60j)" count={age.lt60} pct={pct(age.lt60)} color="bg-amber-400" />
            <AgeBar label="Longues (60–180j)" count={age.lt180} pct={pct(age.lt180)} color="bg-orange-500" />
            <AgeBar label="Très longues (> 180j)" count={age.gt180} pct={pct(age.gt180)} color="bg-red-500" />
          </div>
        )}
      </div>

      {/* Baisses de prix */}
      {stats.priceDrops.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
            Baisses de prix détectées
          </h3>
          <div className="space-y-1">
            {stats.priceDrops.slice(0, 5).map((p) => (
              <div key={p.id} className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">{p.city ?? '—'}</span>
                <span className="text-gray-400 line-through mr-2">{fmt(p.originalPrice)} CHF</span>
                <span className="font-medium text-gray-800 dark:text-gray-200">{fmt(p.currentPrice)} CHF</span>
                <span className="ml-2 text-green-600 font-semibold">−{p.dropPct}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rotation par ville */}
      {stats.byCity.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
            Rotation par ville (top 8)
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-400 uppercase">
                <th className="text-left pb-1">Ville</th>
                <th className="text-right pb-1">Total</th>
                <th className="text-right pb-1">Off-market</th>
                <th className="text-right pb-1">Taux</th>
              </tr>
            </thead>
            <tbody>
              {stats.byCity
                .filter((c) => c.total >= 2)
                .slice(0, 8)
                .map((c) => (
                  <tr key={c.city} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="py-1 text-gray-700 dark:text-gray-300">{c.city}</td>
                    <td className="py-1 text-right text-gray-500">{c.total}</td>
                    <td className="py-1 text-right text-gray-500">{c.offMarket}</td>
                    <td className="py-1 text-right font-medium">
                      <span
                        className={
                          c.offMarketRate >= 50
                            ? 'text-green-600'
                            : c.offMarketRate >= 25
                            ? 'text-amber-500'
                            : 'text-gray-400'
                        }
                      >
                        {c.offMarketRate}%
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ─── sous-composants ─── */

const KpiCard: FC<{ label: string; value: number | string; color?: 'green' | 'blue' | 'gray' }> = ({
  label,
  value,
  color,
}) => {
  const valueClass =
    color === 'green'
      ? 'text-green-600 dark:text-green-400'
      : color === 'blue'
      ? 'text-blue-600 dark:text-blue-400'
      : color === 'gray'
      ? 'text-gray-500 dark:text-gray-400'
      : 'text-gray-800 dark:text-gray-200';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-2xl font-bold ${valueClass}`}>{value}</p>
    </div>
  );
};

const AgeBar: FC<{ label: string; count: number; pct: number; color: string }> = ({
  label,
  count,
  pct,
  color,
}) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-gray-500 dark:text-gray-400 w-36 shrink-0">{label}</span>
    <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
      <div className={`${color} h-2.5 rounded-full transition-all`} style={{ width: `${pct}%` }} />
    </div>
    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 w-14 text-right">
      {count} ({pct}%)
    </span>
  </div>
);
